import { useState } from 'react';
import { can, canHoldVisits, localWallTimeUtc, type Role } from '@servwell/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTRPC } from '../../lib/trpc';
import { createIntentKey } from '../../lib/idempotency';
import { useOrgClock } from '../../lib/useOrgClock';
import { useSession } from '../session';
import { useOnline } from '../ui/useConnection';
import { Sheet } from '../ui/Sheet';
import { Button } from '../ui/Button';
import { Notice } from '../ui/Badge';
import { Field, field } from '../create/Field';
import {
  StaleNotice,
  UnresolvedState,
  refreshFailed,
  shownState,
} from '../ui/QueryStates';
import { IconTeam } from '../icons';

/**
 * Putting a visit on an existing job (V5 dogfood — `visit.create` was reachable only at job
 * creation, so the job page told people to "schedule one" with no way to do it, and a job
 * needing a second trip had nowhere to record it).
 *
 * A second trip is ordinary in this trade: parts weren't on the truck, the customer wasn't
 * home, the repair needs two people. Every one of those is a new visit on the SAME job, so
 * the costs and the history stay together.
 */
export function AddVisitSheet({
  jobId,
  open,
  onClose,
}: {
  jobId: string;
  open: boolean;
  onClose: () => void;
}) {
  const trpc = useTRPC();
  const qc = useQueryClient();
  const session = useSession();
  const online = useOnline();
  const clock = useOrgClock();
  const members = useQuery(trpc.org.members.queryOptions());
  const calendar = useQuery({
    ...trpc.scheduleAvailability.calendar.queryOptions(),
    enabled: open,
  });
  const dispatchable = (members.data ?? []).filter((m) => canHoldVisits(m.role as Role));
  const [when, setWhen] = useState('');
  const [assignees, setAssignees] = useState<string[]>([]);
  const [duration, setDuration] = useState('');
  const [instructions, setInstructions] = useState('');
  const [override, setOverride] = useState(false);
  const [reason, setReason] = useState('');
  const [keys] = useState(() => createIntentKey());
  const [requested, setRequested] = useState<{
    startsAt: Date;
    endsAt: Date;
    userIds: string[];
  } | null>(null);
  const availability = useQuery({
    ...trpc.scheduleAvailability.availability.queryOptions(
      requested ?? { startsAt: new Date(0), endsAt: new Date(1) },
    ),
    enabled: requested !== null,
  });

  const create = useMutation(
    trpc.visit.create.mutationOptions({
      onSuccess: () => {
        void qc.invalidateQueries();
        setWhen('');
        setAssignees([]);
        setDuration('');
        setOverride(false);
        setReason('');
        setRequested(null);
        keys.clear();
        setInstructions('');
        onClose();
      },
    }),
  );
  const selectedDuration =
    duration || String(calendar.data?.calendar?.default_duration_minutes ?? '');
  const interval = () => {
    if (
      !when ||
      !clock.tz ||
      !selectedDuration ||
      !Number.isFinite(Number(selectedDuration))
    )
      return null;
    const [day, time] = when.split('T');
    const [hour, minute] = time!.split(':').map(Number);
    const startsAt = localWallTimeUtc(day!, hour! * 60 + minute!, clock.tz);
    return {
      startsAt,
      endsAt: new Date(startsAt.getTime() + Number(selectedDuration) * 60_000),
    };
  };
  const showOverride =
    can(session.role, 'visit', 'update') &&
    (create.error?.data?.code === 'CONFLICT' ||
      availability.data?.crew.some((member) => member.state === 'conflict'));
  const membersState = shownState(members, members.data);
  const availabilityState = shownState(availability, availability.data);
  const resetCheck = () => {
    setRequested(null);
    setOverride(false);
    setReason('');
    create.reset();
  };
  const save = () => {
    const times = interval();
    if (!online || create.isPending || !times) return;
    const payload = {
      jobId,
      scheduledStartAt: times.startsAt,
      scheduledEndAt: times.endsAt,
      assigneeUserIds: assignees,
      instructions: instructions.trim() || undefined,
      ...(override && showOverride
        ? { scheduleOverride: { reason: reason.trim() } }
        : {}),
    };
    create.mutate({
      ...payload,
      ...keys.forIntent([session.orgId, session.userId, payload]),
    });
  };

  return (
    <Sheet
      open={open}
      onClose={() => {
        if (!create.isPending) onClose();
      }}
      title="Schedule a visit"
      footer={
        <Button
          className="w-full"
          disabled={
            !online ||
            !when ||
            !selectedDuration ||
            clock.state !== 'ready' ||
            create.isPending ||
            (override && !reason.trim())
          }
          onClick={save}
        >
          {create.isPending ? 'Scheduling…' : 'Schedule visit'}
        </Button>
      }
    >
      <div className="space-y-3">
        {!online ? (
          <Notice tone="warn">
            Reconnect before booking a crew. The server checks the current schedule when
            saving.
          </Notice>
        ) : null}
        {create.error ? <Notice tone="danger">{create.error.message}</Notice> : null}
        {clock.state !== 'ready' ? (
          <UnresolvedState
            state={clock.state}
            icon={IconTeam}
            what="the shop's timezone"
            onRetry={clock.refetch}
          />
        ) : null}

        <Field label={`Start${clock.tz ? ` · ${clock.tz}` : ''}`}>
          <input
            className={field}
            type="datetime-local"
            value={when}
            disabled={create.isPending}
            onChange={(e) => {
              setWhen(e.target.value);
              resetCheck();
            }}
            autoFocus
          />
        </Field>

        <Field label="Visit duration in minutes">
          <input
            className={field}
            type="number"
            min={1}
            max={44640}
            value={selectedDuration}
            disabled={create.isPending}
            onChange={(event) => {
              setDuration(event.target.value);
              resetCheck();
            }}
          />
        </Field>
        <fieldset disabled={create.isPending} className="space-y-1">
          <legend className="text-sm font-medium">Who's going</legend>
          <p className="text-xs text-muted-foreground">
            Leave everyone unchecked to schedule an unassigned visit.
          </p>
          <StaleNotice
            show={refreshFailed(members, members.data) || members.isPaused}
            onRetry={() => void members.refetch()}
          />
          {membersState !== 'ready' ? (
            <UnresolvedState
              state={membersState}
              icon={IconTeam}
              what="crew members"
              error={members.error}
              onRetry={() => void members.refetch()}
            />
          ) : (
            dispatchable.map((member) => (
              <label
                key={member.userId}
                className="flex min-h-11 items-center gap-2 text-sm"
              >
                <input
                  type="checkbox"
                  checked={assignees.includes(member.userId)}
                  onChange={(event) => {
                    setAssignees(
                      event.target.checked
                        ? [...assignees, member.userId]
                        : assignees.filter((id) => id !== member.userId),
                    );
                    resetCheck();
                  }}
                />
                {member.name ?? member.email}
              </label>
            ))
          )}
        </fieldset>
        <Button
          variant="ghost"
          disabled={
            !online ||
            !when ||
            !selectedDuration ||
            !assignees.length ||
            clock.state !== 'ready' ||
            create.isPending
          }
          onClick={() => {
            const times = interval();
            if (times) setRequested({ ...times, userIds: assignees });
          }}
        >
          Check crew availability
        </Button>
        {requested ? (
          <section aria-label="Crew availability result" className="space-y-2">
            <StaleNotice
              show={
                refreshFailed(availability, availability.data) || availability.isPaused
              }
              onRetry={() => void availability.refetch()}
            />
            {availabilityState !== 'ready' ? (
              <UnresolvedState
                state={availabilityState}
                icon={IconTeam}
                what="crew availability"
                error={availability.error}
                onRetry={() => void availability.refetch()}
              />
            ) : availability.data ? (
              <>
                <p className="text-xs text-muted-foreground">
                  {availability.data.travelBufferMinutes} minute buffer between adjacent
                  visits. Availability is checked again when booking.
                </p>
                <ul>
                  {availability.data.crew.map((member) => (
                    <li
                      key={member.userId}
                      className="border-b border-border py-2 text-sm"
                    >
                      {member.name}: <strong>{member.state}</strong>
                      {member.conflicts.length ? (
                        <p className="text-xs text-muted-foreground">
                          {member.conflicts
                            .map((conflict) => conflict.kind.replaceAll('_', ' '))
                            .join(' · ')}
                        </p>
                      ) : null}
                    </li>
                  ))}
                </ul>
              </>
            ) : null}
          </section>
        ) : null}
        {showOverride ? (
          <fieldset className="space-y-2" disabled={create.isPending}>
            <label className="flex min-h-11 items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={override}
                onChange={(event) => setOverride(event.target.checked)}
              />
              Record an authorized schedule override
            </label>
            {override ? (
              <Field label="Reason for overriding the conflict">
                <textarea
                  className={`${field} min-h-24`}
                  value={reason}
                  maxLength={500}
                  required
                  onChange={(event) => setReason(event.target.value)}
                />
              </Field>
            ) : null}
          </fieldset>
        ) : null}

        <Field label="Anything they need to know">
          <textarea
            className="min-h-[70px] w-full rounded-[var(--radius)] border border-border bg-surface p-2 text-sm"
            value={instructions}
            disabled={create.isPending}
            onChange={(e) => setInstructions(e.target.value)}
            placeholder="Bring the 3-ton condenser fan motor — it wasn't on the truck last time."
          />
        </Field>
      </div>
    </Sheet>
  );
}
